"use client";
import React, { useState } from "react";
import { SectionHeading } from "@/components/common/decor";
import { ButtonCta } from "@/components";

const cases = [
  {
    label: "Children's Illustrated Book",
    before: "/images/before.webp",
    after: "/images/after1.webp",
    note: "From handwritten story notes to a fully illustrated, print-ready picture book.",
  },
  {
    label: "Storyboard to Finished Spread",
    before: "/images/before2.webp",
    after: "/images/after2.webp",
    note: "Rough sketch storyboards developed into polished, publication-ready interior artwork.",
  },
  {
    label: "Concept Sketch to Final Cover",
    before: "/images/before3.webp",
    after: "/images/after3.webp",
    note: "An early cover concept refined into a retail-ready, genre-true book cover.",
  },
];

export default function Transformation() {
  const [show, setShow] = useState(cases.map(() => true)); // true = after

  return (
    <section className="relative overflow-hidden bg-cream px-4 py-20 md:px-8">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "url('/images/noise.svg')" }}
      />
      <div className="relative mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Before & After"
          title="Experience the Transformation, Draft to Finished Book"
          description="Click any project to see how it came together — the same craft we bring to manuscripts, storyboards, and cover art."
          className="mb-14"
        />

        <div className="grid grid-cols-1 gap-7 md:grid-cols-3">
          {cases.map((c, i) => (
            <div
              key={c.label}
              data-aos="fade-up"
              data-aos-delay={i * 100}
              className="group overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-card"
            >
              <button
                onClick={() =>
                  setShow((s) => s.map((v, idx) => (idx === i ? !v : v)))
                }
                className="relative block h-72 w-full overflow-hidden"
              >
                <img loading="lazy" decoding="async"
                  src={c.before}
                  alt={`${c.label} before`}
                  className="absolute inset-0 h-full w-full bg-ink/5 object-contain p-4"
                />
                <img loading="lazy" decoding="async"
                  src={c.after}
                  alt={`${c.label} after`}
                  className={`absolute inset-0 h-full w-full object-contain p-4 transition-opacity duration-700 ${
                    show[i] ? "opacity-100" : "opacity-0"
                  }`}
                />
                <span
                  className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    show[i] ? "bg-gold text-ink" : "bg-ink/70 text-white"
                  }`}
                >
                  {show[i] ? "After" : "Before"}
                </span>
                <span className="absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-ink shadow transition-transform group-hover:rotate-180">
                  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                    <path d="M4 12a8 8 0 0 1 14-5m2-2v4h-4M20 12a8 8 0 0 1-14 5m-2 2v-4h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              <div className="p-6">
                <h3 className="font-display text-lg font-semibold text-ink">{c.label}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">{c.note}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <ButtonCta buttonText="See More Transformations" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
        </div>
      </div>
    </section>
  );
}
